import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { X, ChevronLeft, ChevronRight, MousePointerClick } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTutorialStore, getTutorialSteps, TutorialStep } from '../../store/tutorialStore';
import { useSettingsStore } from '../../store/settingsStore';
import { clsx } from 'clsx';

interface TargetRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface TooltipPosition {
  top: number;
  left: number;
}

const TOOLTIP_WIDTH = 340;
const TOOLTIP_HEIGHT_FALLBACK = 200;
/** Délai avant de sauter une étape dont la cible facultative reste introuvable. */
const SKIP_AFTER_MS = 1200;
/** Délai avant d'afficher le pas au centre, cible introuvable : jamais de blocage. */
const DETACH_AFTER_MS = 2500;

/** Rend les `**passages en gras**` du texte de l'étape. */
function renderRichText(text: string) {
  return text.split(/(\*\*[^*]+\*\*)/g).map((chunk, i) =>
    chunk.startsWith('**') && chunk.endsWith('**') ? (
      <strong key={i} style={{ color: 'var(--text)', fontWeight: 600 }}>
        {chunk.slice(2, -2)}
      </strong>
    ) : (
      chunk
    )
  );
}

export default function TutorialOverlay() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    isActive,
    currentStepIndex,
    nextStep,
    previousStep,
    exitTutorial,
    completeTutorial,
  } = useTutorialStore();

  const { t } = useTranslation('common');
  const { t: tt } = useTranslation('tutorial');
  const accountType = useSettingsStore((s) => s.settings?.accountType);
  const steps = useMemo(() => getTutorialSteps(accountType), [accountType]);

  const [targetRect, setTargetRect] = useState<TargetRect | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<TooltipPosition>({ top: 0, left: 0 });
  const [isTransitioning, setIsTransitioning] = useState(false);
  // Cible restée introuvable : le pas s'affiche au centre, sans découpe
  const [isDetached, setIsDetached] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  // Sens de progression, pour sauter les étapes facultatives dans le bon sens
  const directionRef = useRef<1 | -1>(1);

  const currentStep: TutorialStep | undefined = steps[currentStepIndex];
  const isLastStep = currentStepIndex === steps.length - 1;
  const isFirstStep = currentStepIndex === 0;

  const goNext = useCallback(() => {
    directionRef.current = 1;
    if (isLastStep) completeTutorial();
    else nextStep();
  }, [isLastStep, completeTutorial, nextStep]);

  const goPrevious = useCallback(() => {
    directionRef.current = -1;
    previousStep();
  }, [previousStep]);

  // Calculer la position du tooltip
  const calculateTooltipPosition = useCallback((rect: TargetRect, position: TutorialStep['position']) => {
    const tooltipWidth = TOOLTIP_WIDTH;
    const tooltipHeight = tooltipRef.current?.offsetHeight || TOOLTIP_HEIGHT_FALLBACK;
    const padding = 16;
    const arrowOffset = 12;

    let top = 0;
    let left = 0;

    switch (position) {
      case 'top':
        top = rect.top - tooltipHeight - arrowOffset;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
        break;
      case 'bottom':
        top = rect.top + rect.height + arrowOffset;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
        break;
      case 'left':
        top = rect.top + rect.height / 2 - tooltipHeight / 2;
        left = rect.left - tooltipWidth - arrowOffset;
        break;
      case 'right':
        top = rect.top + rect.height / 2 - tooltipHeight / 2;
        left = rect.left + rect.width + arrowOffset;
        break;
    }

    // S'assurer que le tooltip reste visible
    left = Math.max(padding, Math.min(left, window.innerWidth - tooltipWidth - padding));
    top = Math.max(padding, Math.min(top, window.innerHeight - tooltipHeight - padding));

    return { top, left };
  }, []);

  // Trouver un élément parmi tous les éléments correspondants (même hors viewport)
  const findVisibleElement = useCallback((selector: string): Element | null => {
    const elements = document.querySelectorAll(selector);
    for (const element of elements) {
      const rect = element.getBoundingClientRect();
      // Vérifier que l'élément existe et a une taille (peut être hors viewport pour le scroll)
      if (rect.width > 0 && rect.height > 0) {
        return element;
      }
    }
    return null;
  }, []);

  // Trouver et positionner l'élément cible ; `false` si la cible n'existe pas (encore)
  const findAndPositionTarget = useCallback((): boolean => {
    if (!currentStep) return false;

    const element = findVisibleElement(currentStep.targetSelector);
    if (!element) {
      setTargetRect(null);
      return false;
    }

    // Scroller l'élément en vue si nécessaire
    const rect = element.getBoundingClientRect();
    const isInViewport = rect.top >= 0 && rect.bottom <= window.innerHeight;
    if (!isInViewport) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Attendre la fin du scroll avant de calculer la position
      setTimeout(() => {
        const newRect = element.getBoundingClientRect();
        const scrolled = {
          top: newRect.top,
          left: newRect.left,
          width: newRect.width,
          height: newRect.height,
        };
        setTargetRect(scrolled);
        setTooltipPosition(calculateTooltipPosition(scrolled, currentStep.position));
      }, 400);
      return true;
    }

    const newRect = {
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
    };
    setTargetRect(newRect);
    setTooltipPosition(calculateTooltipPosition(newRect, currentStep.position));
    return true;
  }, [currentStep, calculateTooltipPosition, findVisibleElement]);

  // Naviguer vers la bonne page si nécessaire (query comprise : `?tab=...`)
  useEffect(() => {
    if (!isActive || !currentStep?.page) return;

    const [targetPath, targetQuery] = currentStep.page.split('?');
    const onPath = location.pathname === targetPath;
    // Sans query dans l'étape, la query courante est libre (compte sélectionné…)
    const onQuery = !targetQuery || location.search.replace(/^\?/, '') === targetQuery;

    if (!onPath || !onQuery) {
      setIsTransitioning(true);
      navigate(currentStep.page);
    }
  }, [isActive, currentStep, location.pathname, location.search, navigate]);

  // Chercher l'élément cible, puis suivre ses déplacements
  useEffect(() => {
    if (!isActive || !currentStep) return;

    setIsDetached(false);
    let missingSince: number | null = null;

    const tick = () => {
      const found = findAndPositionTarget();
      if (found) {
        missingSince = null;
        setIsTransitioning(false);
        setIsDetached(false);
        return;
      }
      const now = Date.now();
      if (missingSince === null) {
        missingSince = now;
        return;
      }
      const elapsed = now - missingSince;
      // Cible facultative (absente selon le plan ou le mode de gestion) : on passe
      if (currentStep.optional && elapsed > SKIP_AFTER_MS) {
        if (directionRef.current === -1 && !isFirstStep) previousStep();
        else if (!isLastStep) nextStep();
        else completeTutorial();
        return;
      }
      // Filet de sécurité : jamais d'overlay noir sans explication
      if (elapsed > DETACH_AFTER_MS) {
        setIsTransitioning(false);
        setIsDetached(true);
      }
    };

    // Attendre que la page se charge
    const timer = setTimeout(tick, 300);
    const intervalId = setInterval(tick, 400);

    const handleReflow = () => { findAndPositionTarget(); };
    window.addEventListener('resize', handleReflow);
    window.addEventListener('scroll', handleReflow, true);

    return () => {
      clearTimeout(timer);
      clearInterval(intervalId);
      window.removeEventListener('resize', handleReflow);
      window.removeEventListener('scroll', handleReflow, true);
    };
  }, [
    isActive, currentStep, findAndPositionTarget,
    isFirstStep, isLastStep, nextStep, previousStep, completeTutorial,
  ]);

  // Gérer les clics sur l'élément cible pour les étapes interactives
  useEffect(() => {
    if (!isActive || !currentStep?.requiresInteraction) return;

    const element = findVisibleElement(currentStep.targetSelector);
    if (!element) return;

    const handleClick = () => {
      setTimeout(goNext, 100);
    };

    element.addEventListener('click', handleClick);
    return () => element.removeEventListener('click', handleClick);
  }, [isActive, currentStep, goNext, findVisibleElement]);

  // Gérer les touches clavier
  useEffect(() => {
    if (!isActive) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        exitTutorial();
      } else if (e.key === 'ArrowRight' && (!currentStep?.requiresInteraction || isDetached)) {
        goNext();
      } else if (e.key === 'ArrowLeft' && !isFirstStep) {
        goPrevious();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isActive, currentStep, isFirstStep, isDetached, goNext, goPrevious, exitTutorial]);

  if (!isActive || !currentStep) return null;

  // Cible perdue : l'étape reste lisible et franchissable au clavier comme à la souris
  const awaitsClick = currentStep.requiresInteraction && !isDetached;
  const showTooltip = (targetRect && !isTransitioning) || isDetached;

  const handleNext = () => {
    if (awaitsClick) return;
    goNext();
  };

  return (
    <div className="fixed inset-0 z-[9999] pointer-events-none">
      {/* Overlay sombre avec découpe pour l'élément cible */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-auto"
        style={{ transition: 'opacity 0.3s' }}
      >
        <defs>
          <mask id="tutorial-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {targetRect && !isDetached && (
              <rect
                x={targetRect.left - 8}
                y={targetRect.top - 8}
                width={targetRect.width + 16}
                height={targetRect.height + 16}
                rx="8"
                fill="black"
                style={{ transition: 'all 0.3s ease-out' }}
              />
            )}
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(0, 0, 0, 0.7)"
          mask="url(#tutorial-mask)"
        />
      </svg>

      {/* Bordure lumineuse autour de l'élément cible */}
      {targetRect && !isDetached && (
        <div
          className="absolute rounded-lg pointer-events-none transition-all duration-300 ease-out"
          style={{
            top: targetRect.top - 8,
            left: targetRect.left - 8,
            width: targetRect.width + 16,
            height: targetRect.height + 16,
            border: '2px solid var(--pa)',
            boxShadow: '0 0 0 4px color-mix(in oklab, var(--pa) 30%, transparent)',
          }}
        />
      )}

      {/* Rendre l'élément cible cliquable si interaction requise */}
      {targetRect && awaitsClick && (
        <div
          className="absolute pointer-events-auto cursor-pointer"
          style={{
            top: targetRect.top - 8,
            left: targetRect.left - 8,
            width: targetRect.width + 16,
            height: targetRect.height + 16,
          }}
          onClick={() => {
            const element = findVisibleElement(currentStep.targetSelector) as HTMLElement | null;
            if (!element) return;
            // L'ancre peut être posée sur un conteneur : viser l'élément
            // réellement cliquable qu'il enveloppe, sinon rien ne se passe.
            const target =
              (element.matches('a, button')
                ? element
                : element.querySelector<HTMLElement>('a, button')) ?? element;

            // Si c'est un lien, naviguer directement via React Router
            if (target.tagName === 'A') {
              const href = target.getAttribute('href');
              if (href) {
                navigate(href);
                setTimeout(goNext, 100);
                return;
              }
            }
            target.click();
          }}
        />
      )}

      {/* Tooltip */}
      {showTooltip && (
        <div
          ref={tooltipRef}
          className="absolute rounded-xl p-5 pointer-events-auto animate-fade-in"
          style={
            isDetached
              ? {
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: TOOLTIP_WIDTH,
                  background: 'var(--panel)',
                  border: '1px solid var(--line)',
                  boxShadow: 'var(--shadow-lg)',
                  color: 'var(--text)',
                }
              : {
                  top: tooltipPosition.top,
                  left: tooltipPosition.left,
                  width: TOOLTIP_WIDTH,
                  transition: 'top 0.3s ease-out, left 0.3s ease-out',
                  background: 'var(--panel)',
                  border: '1px solid var(--line)',
                  boxShadow: 'var(--shadow-lg)',
                  color: 'var(--text)',
                }
          }
        >
          {/* Bouton fermer */}
          <button
            onClick={exitTutorial}
            className="absolute top-3 right-3 p-1 rounded-lg transition-colors"
            style={{ color: 'var(--text-3)' }}
            title={t('tutorial.exitTutorial')}
          >
            <X className="w-4 h-4" />
          </button>

          {/* Contenu */}
          <div className="pr-6">
            <h3 className="text-base font-semibold mb-2" style={{ color: 'var(--text)' }}>
              {tt(currentStep.titleKey)}
            </h3>
            <p className="text-sm leading-relaxed mb-4" style={{ color: 'var(--text-2)' }}>
              {renderRichText(tt(currentStep.contentKey))}
            </p>
          </div>

          {/* Indication d'interaction */}
          {awaitsClick && (
            <div
              className="flex items-center gap-2 text-sm mb-4 px-3 py-2 rounded-lg"
              style={{
                color: 'var(--pa)',
                background: 'color-mix(in oklab, var(--pa) 12%, transparent)',
              }}
            >
              <MousePointerClick className="w-4 h-4 animate-pulse" />
              <span>{currentStep.hintKey ? tt(currentStep.hintKey) : t('tutorial.clickElement')}</span>
            </div>
          )}

          {/* Navigation */}
          <div
            className="flex items-center justify-between pt-3"
            style={{ borderTop: '1px solid var(--line-soft, var(--line))' }}
          >
            <span className="text-xs" style={{ color: 'var(--text-3)' }}>
              {currentStepIndex + 1} / {steps.length}
            </span>

            <div className="flex gap-2">
              {!isFirstStep && (
                <button
                  onClick={goPrevious}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg transition-colors"
                  style={{ color: 'var(--text-2)' }}
                >
                  <ChevronLeft className="w-4 h-4" />
                  {t('previous')}
                </button>
              )}

              {!awaitsClick && (
                <button
                  onClick={handleNext}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm text-white rounded-lg transition-opacity hover:opacity-90"
                  style={{ background: 'var(--pa)' }}
                >
                  {isLastStep ? t('tutorial.finish') : t('next')}
                  {!isLastStep && <ChevronRight className="w-4 h-4" />}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Message de chargement si transition en cours */}
      {isTransitioning && !showTooltip && (
        <div className={clsx('absolute inset-0 flex items-center justify-center pointer-events-auto')}>
          <div
            className="rounded-xl p-6 text-center"
            style={{ background: 'var(--panel)', border: '1px solid var(--line)', boxShadow: 'var(--shadow-lg)' }}
          >
            <div
              className="animate-spin rounded-full h-8 w-8 mx-auto mb-3"
              style={{ borderBottom: '2px solid var(--pa)' }}
            />
            <p style={{ color: 'var(--text-2)' }}>{t('tutorial.loading')}</p>
          </div>
        </div>
      )}
    </div>
  );
}
